import { useEffect, useState } from "react";
import { FiZap } from "react-icons/fi";
import { FaCrown, FaRegStar } from "react-icons/fa";
import { BsCheckCircle } from "react-icons/bs";
import { apiRequest } from "../../helpers";

const TOKENS_PER_MILLION = 1000000;
const CREDITS_PER_MILLION_TOKENS = 19.11;

export default function TutorSubscriptions() {
  const annualMonthlyPrices = [19, 38, 75];
  const [isAnnual, setIsAnnual] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const billingType = isAnnual ? "ANNUAL" : "MONTHLY";
  const filteredPackages = packages.filter(
    (pkg) => pkg.validityType === billingType
  ).sort((a, b) => a.credits - b.credits);

  useEffect(() => {
    getAIPackages()
  },[])

  const getAIPackages = async () => {
  try {
    const res = await apiRequest({
      method: "get",
      url: "accounts/packages",
    });

    const mapped = res.data.map((pkg) => {
      return {
      id: pkg.id,
      name: pkg.name,
      credits: pkg.tokens,
      monthlyPrice: parseFloat(pkg.price),
      validityType: pkg.validity_type,
      sourceName: pkg.name,
    };
    });

    setPackages(mapped);

    const sorted = [...mapped]
      .filter((p) => p.validityType === "ANNUAL")
      .sort((a, b) => a.credits - b.credits);
    const popular =
      sorted.find((p) => p.name?.toLowerCase().includes("professional")) ||
      sorted[1] ||
      sorted[0];
    if (popular) setSelectedPackage(popular.id);
  } catch (e) {
    console.error(e);
  }
};

  const getPlanMeta = (name, position) => {
    const lowerName = name?.toLowerCase() || "";

    if (lowerName.includes("professional") || position === 1) {
      return {
        name: "Professional",
        description: "Ideal for dedicated students",
        popular: true,
        icon: FaRegStar,
        iconBg: "#fff1e8",
        features: (tokens) => [
          `${tokens} credits per month`,
          "Access to Clara AI tutor and career mentor",
          "Upload your course materials in AI",
          "Premium support",
        ],
      };
    }

    if (lowerName.includes("expert") || position >= 2) {
      return {
        name: "Expert",
        description: "For intensive learning",
        popular: false,
        icon: FaCrown,
        iconBg: "#f4f5f7",
        features: (tokens) => [
          `${tokens} credits per month`,
          "Access to Clara AI tutor and career mentor",
          "Upload your course materials in AI",
          "Priority support",
        ],
      };
    }

    return {
      name: "Starter",
      description: "Perfect for casual learners",
      popular: false,
      icon: FiZap,
      iconBg: "#f4f5f7",
      features: (tokens) => [
        `${tokens} credits per month`,
        "Access to Clara AI tutor and career mentor",
        "Upload your course materials in AI",
        "Standard support",
      ],
    };
  };

  const getPrice = (monthlyPrice) => {
    if (isAnnual) {
      return Math.round(monthlyPrice * 0.75); // 25% discount
    }
    return monthlyPrice;
  };

  const getSavings = (monthlyPrice) => {
    return Math.round(monthlyPrice * 0.25);
  };

  const getDisplayCredits = (pkg) => {
    return Math.round(
      (pkg.credits / TOKENS_PER_MILLION) * CREDITS_PER_MILLION_TOKENS
    );
  };

  const handleSelect = async (pkg) => {
    setSelectedPackage(pkg.id);
    setCheckoutLoading(true);

    try {
      const res = await apiRequest({
        method: "post",
        url: "accounts/packages/checkout/",
        data: {
          package_id: pkg.id,
          success_url:
            window.location.origin +
            "/aitutor/creditsbot?token_checkout_session_id={CHECKOUT_SESSION_ID}",
          cancel_url: window.location.href,
        },
      });

      const checkoutUrl = res?.data?.checkout_url || res?.data?.sessionId;
      if (checkoutUrl) {
        window.location.replace(checkoutUrl);
        return;
      }

      setCheckoutLoading(false);
    } catch (e) {
      console.error(e);
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen font-inter mt-24" style={{ backgroundColor: "#fff7ef" }}>
      <div className="max-w-7xl mx-auto px-6 py-8 md:py-12">
        <div className="flex items-center justify-center gap-6 mb-20">
          <span
            className={`text-xl font-bold ${
              !isAnnual ? "text-primary" : "text-gray-500"
            }`}
            style={{ fontSize: "1.25rem" }}
          >
            Monthly
          </span>
          <div
            onClick={() => {
              setIsAnnual(!isAnnual);
              setSelectedPackage(null);
            }}
            className={`relative w-14 h-8 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-300 ${
              isAnnual ? "bg-primary" : "bg-gray"
            }`}
            style={{ backgroundColor: isAnnual ? "#ff6b14" : "#d1d5db" }}
          >
            <div
              className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                isAnnual ? "translate-x-6" : "translate-x-0"
              }`}
            />
          </div>
          <span
            className={`text-xl font-bold ${
              isAnnual ? "text-primary" : "text-gray-500"
            }`}
            style={{ color: isAnnual ? "#ff6b14" : "#6b7280", fontSize: "1.25rem" }}
          >
            Annual
          </span>
          {isAnnual && (
            <div
              className="px-5 py-2 rounded-full text-sm font-semibold ml-3"
              style={{
                backgroundColor: "#fff1e8",
                border: "1px solid #ffd6bd",
                color: "#ff6b14",
                fontSize: "0.95rem",
              }}
            >
              Save 25%
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto items-stretch">
          {filteredPackages.map((pkg, index) => {
            const meta = getPlanMeta(pkg.sourceName, index);
            const Icon = meta.icon;
            const displayCredits = getDisplayCredits(pkg);
            const displayPrice = isAnnual
              ? annualMonthlyPrices[index] ?? getPrice(pkg.monthlyPrice)
              : pkg.monthlyPrice;
            const yearlyPrice = displayPrice * 12;
            const isSelected = selectedPackage === pkg.id;
            const isPopular = meta.popular;

            return(
            <div
              key={index}
              className="relative transition-all duration-200 flex flex-col"
              onClick={() => setSelectedPackage(pkg.id)}
              style={{
                minHeight: "760px",
                padding: "3.75rem 3.75rem 3rem",
                backgroundColor: "#fff",
                borderRadius: "16px",
                border: isSelected ? "2px solid #ff8a3d" : "1px solid #e6e8ec",
                boxShadow: isSelected
                  ? "0 18px 35px rgba(255, 107, 20, 0.12)"
                  : "0 10px 26px rgba(15, 23, 42, 0.12)",
                cursor: "pointer",
              }}
            >
              {isPopular && (
                <div
                  className="absolute left-1/2 transform -translate-x-1/2 text-white font-bold"
                  style={{
                    top: "-18px",
                    backgroundColor: "#ff6b14",
                    borderRadius: "9999px",
                    padding: "0.8rem 3rem",
                    fontSize: "1rem",
                    minWidth: "180px",
                    textAlign: "center",
                  }}
                >
                  Most Popular
                </div>
              )}

              <div className="text-center mb-10">
                <div
                  className="mx-auto flex items-center justify-center"
                  style={{
                    width: "82px",
                    height: "82px",
                    borderRadius: "16px",
                    backgroundColor: meta.iconBg,
                    marginBottom: "2.5rem",
                  }}
                >
                  <Icon
                    style={{
                      fontSize: "2.3rem",
                      color: isPopular ? "#ff6b14" : "#4b5563",
                    }}
                  />
                </div>
                <h3
                  className="font-bold mb-5"
                  style={{ fontSize: "1.75rem", color: "#1f2937" }}
                >
                  {meta.name}
                </h3>
                <p
                  style={{
                    fontSize: "1.25rem",
                    color: "#4b5563",
                    lineHeight: 1.4,
                  }}
                >
                  {meta.description}
                </p>
              </div>

              <div className="text-center mb-8">
                <div
                  className="font-bold mb-3"
                  style={{ fontSize: "3.9rem", color: "#ff6b14", lineHeight: 1 }}
                >
                  {displayCredits}
                </div>
                <p
                  className="font-semibold"
                  style={{ fontSize: "1.05rem", color: "#4b5563" }}
                >
                  credits per month
                </p>
              </div>

              <div className="text-center mb-10">
                <div className="flex items-baseline justify-center gap-1">
                  <span
                    className="font-bold"
                    style={{ fontSize: "2.7rem", color: "#1f2937", lineHeight: 1 }}
                  >
                    ${displayPrice}
                  </span>
                  <span style={{ fontSize: "1.35rem", color: "#4b5563" }}>/month</span>
                </div>
                {isAnnual && (
                  <p
                    className="font-semibold mt-4"
                    style={{ fontSize: "1rem", color: "#6b7280" }}
                  >
                    ${yearlyPrice}/year
                  </p>
                )}
                {isAnnual && (
                  <p
                    className="mt-3"
                    style={{ color: "#16a34a", fontWeight: 700, fontSize: "1rem" }}
                  >
                    Save ${getSavings(pkg.monthlyPrice)}/month
                  </p>
                )}
                {/* {!isAnnual && (
                  <div className="text-gray-500 mt-4" style={{ fontSize: "1rem" }}>
                    $
                    {meta.name === "Professional"
                      ? "0.22"
                      : (
                          Math.round((pkg.monthlyPrice / pkg.credits) * 100) /
                          100
                        ).toFixed(2)}{" "}
                    per credit
                  </div>
                )} */}
              </div>

              <div className="space-y-5 mb-10">
                {meta.features(displayCredits).map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start gap-4">
                    <BsCheckCircle
                      className="flex-shrink-0 mt-1"
                      style={{ color: "#ff6b14", fontSize: "1.25rem" }}
                    />
                    <span
                      style={{
                        color: "#374151",
                        fontSize: "1.05rem",
                        lineHeight: 1.35,
                      }}
                    >
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex justify-center mt-auto">
                <button
                  type="button"
                  disabled={checkoutLoading}
                  onClick={(e) => { e.stopPropagation(); handleSelect(pkg); }}
                  style={{
                    width: "100%",
                    padding: "1rem 1.25rem",
                    borderRadius: "12px",
                    border: isSelected ? "none" : "1px solid #e5e7eb",
                    backgroundColor: isSelected ? "#ff6b14" : "#f3f4f6",
                    color: isSelected ? "#fff" : "#374151",
                    fontSize: "1.15rem",
                    fontWeight: 800,
                    cursor: checkoutLoading ? "not-allowed" : "pointer",
                    boxShadow: isSelected ? "0 14px 24px rgba(255, 107, 20, 0.2)" : "none",
                  }}
                >
                  {checkoutLoading && selectedPackage === pkg.id ? "Redirecting..." : "Get Started"}
                </button>
              </div>
            </div>
            )
          })}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <h2
          className="text-center font-bold mb-10"
          style={{ fontSize: "2rem", color: "#1f2937" }}
        >
          Frequently Asked Questions
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            {
              q: "What happens if I run out of credits?",
              a: "You can always purchase additional credits or upgrade your plan. Your learning never has to stop!",
            },
            {
              q: "Can I change my plan anytime?",
              a: "Yes! You can upgrade, downgrade, or cancel your subscription at any time from your account settings.",
            },
            {
              q: "Do unused credits roll over?",
              a: "Yes! Unused credits from your monthly allowance will roll over.",
            },
            {
              q: "Is there a free trial?",
              a: "No, unfortunately we cannot give free trials due to AI server costs we have to incur to provide this service for you. You can get the starter package which is just $25 to try out our AI. You can cancel anytime.",
            },
          ].map((faq, i) => (
            <div
              key={i}
              style={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                padding: "2rem 2.25rem",
                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.07)",
              }}
            >
              <h3
                className="font-bold mb-3"
                style={{ fontSize: "1.1rem", color: "#1f2937" }}
              >
                {faq.q}
              </h3>
              <p style={{ fontSize: "1rem", color: "#4b5563", lineHeight: 1.6 }}>
                {faq.a}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
